import React, { memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faArrowCircleRight,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

const JOB_STATUS_CLASS_MAP = new Map([
  [
    'COMPLETED',
    { text: 'alert-confirmation', background: 'rgba(116, 209, 125, 0.24)' },
  ],
  ['FAILED', { text: 'active-dm', background: 'rgba(222, 71, 58, 0.24)' }],
  [
    'MESSAGE_SENT',
    { text: 'in-progress-yellow', background: 'rgba(196, 146, 33, 0.24)' },
  ],
  ['RUNNING', { text: 'in-progress-yellow' }],
  ['PENDING', { text: 'text-dark' }],
]);

const STATUS_DISPLAY_MAP = new Map([
  ['COMPLETED', 'Complete'],
  ['FAILED', 'Failed'],
  ['MESSAGE_SENT', 'Message Sent'],
  ['RUNNING', 'Running'],
  ['PENDING', 'Pending'],
]);

function CommandHistoryBody({ data }) {
  return data.map((job) => {
    const { jobName, jobMessage, _id, updatedAt, email, status } = job;
    const { text, background } = JOB_STATUS_CLASS_MAP.get(status) || {};
    const pillHasBackground = ['COMPLETED', 'FAILED', 'MESSAGE_SENT'].includes(
      status
    );
    return (
      <tr key={_id}>
        <td className="align-middle">
          <div
            className={`command-status-pill rounded-pill text-${text} ${
              pillHasBackground ? '' : `border border-${text} bg-transparent`
            } three-quarter-text d-inline py-1 px-3`}
          >
            <FontAwesomeIcon icon={faCircle} className="mr-2" size="xs" />
            <span>{STATUS_DISPLAY_MAP.get(status)}</span>
          </div>
        </td>
        <td className="align-middle three-quarter-text">{jobName}</td>
        <td className="three-quarter-text align-middle">
          {(() => {
            switch (status) {
              case 'PENDING':
                return (
                  <FontAwesomeIcon
                    icon={faArrowCircleRight}
                    className="text-text-dark"
                    size="lg"
                  />
                );
              case 'RUNNING':
                return (
                  <FontAwesomeIcon
                    icon={faCircleNotch}
                    className="text-in-progress-yellow"
                    size="lg"
                  />
                );
              case 'MESSAGE_SENT':
              case 'COMPLETED':
              case 'FAILED':
                return (
                  <span>
                    <span
                      className={`font-weight-bolder ${
                        status === 'COMPLETED'
                          ? 'text-text-dark'
                          : `text-${text}`
                      }`}
                    >
                      {`${moment(updatedAt).toNow(true)} ago`}
                    </span>
                    <span
                      className={`ml-2 ${
                        status === 'COMPLETED'
                          ? 'text-text-light-gray'
                          : `text-${text}`
                      }`}
                    >
                      {moment(updatedAt).format('MMM Do - h:mma')}
                    </span>
                  </span>
                );
              default:
                return null;
            }
          })()}
        </td>
        <td className="align-middle three-quarter-text">{email}</td>
        <td className="align-middle three-quarter-text">
          {(() => {
            switch (status) {
              case 'COMPLETED':
                return jobMessage || 'success';
              case 'FAILED':
                return jobMessage || 'failed';
              case 'PENDING':
                return (
                  <FontAwesomeIcon
                    icon={faArrowCircleRight}
                    className="text-text-dark"
                    size="lg"
                  />
                );
              case 'MESSAGE_SENT':
                return jobMessage || 'message sent';
              case 'RUNNING':
                return (
                  <FontAwesomeIcon
                    icon={faCircleNotch}
                    className="text-in-progress-yellow"
                    size="lg"
                  />
                );
              default:
                return null;
            }
          })()}
        </td>
        <style jsx>{`
          .command-status-pill {
            background-color: ${pillHasBackground ? background : ''};
          }
        `}</style>
      </tr>
    );
  });
}

CommandHistoryBody.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      commandId: PropTypes.string,
      createdAt: PropTypes.string,
      email: PropTypes.string,
      gatewayId: PropTypes.string,
      jobCommand: PropTypes.string,
      jobMessage: PropTypes.string,
      jobName: PropTypes.string,
      status: PropTypes.oneOf([
        'COMPLETED',
        'FAILED',
        'MESSAGE_SENT',
        'RUNNING',
        'PENDING',
      ]),
      templateId: PropTypes.string,
      updatedAt: PropTypes.string,
    })
  ),
};

CommandHistoryBody.defaultProps = { data: [] };

export default memo(CommandHistoryBody);
